import React, { useState } from "react";
import { TopNavbar } from "@/components/layout";

export const BulkAssignments: React.FC = () => {
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState("");

  const mockTasks = [
    {
      id: "1",
      title: "Bridge Inspection #1245",
      type: "Bridge",
      location: "Main St Bridge",
      status: "Pending",
    },
    {
      id: "2",
      title: "Road Maintenance #1246",
      type: "Road",
      location: "Highway 101",
      status: "Pending",
    },
    {
      id: "3",
      title: "Conduit Inspection #1247",
      type: "Conduit",
      location: "Industrial Ave",
      status: "Pending",
    },
    {
      id: "4",
      title: "Bridge Inspection #1248",
      type: "Bridge",
      location: "River Crossing",
      status: "Pending",
    },
    {
      id: "5",
      title: "Road Maintenance #1249",
      type: "Road",
      location: "Oak Street",
      status: "Pending",
    },
  ];

  const mockUsers = [
    { id: "1", name: "John Smith", role: "Inspector" },
    { id: "2", name: "Sarah Johnson", role: "Supervisor" },
    { id: "3", name: "Mike Davis", role: "Inspector" },
    { id: "4", name: "Lisa Wilson", role: "Technician" },
  ];

  const handleTaskSelection = (taskId: string) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === mockTasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(mockTasks.map((task) => task.id));
    }
  };

  const handleBulkAssign = () => {
    if (selectedTasks.length === 0 || !selectedUser) {
      alert("Please select tasks and a user to assign to.");
      return;
    }

    const selectedUserName = mockUsers.find(
      (user) => user.id === selectedUser
    )?.name;
    alert(
      `Successfully assigned ${selectedTasks.length} tasks to ${selectedUserName}`
    );

    // Reset selections
    setSelectedTasks([]);
    setSelectedUser("");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        <TopNavbar className="" />

        <div className="max-w-7xl mx-4 my-6">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-custom-text-onyx mb-2">
              Bulk Assignments
            </h1>
            <p className="text-custom-text-gray-dark">
              Assign multiple tasks to team members efficiently.
            </p>
          </div>

          {/* Assignment Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-custom-text-onyx mb-2">
                  Assign to User
                </label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-custom-marian-blue focus:border-transparent"
                >
                  <option value="">Select a user...</option>
                  {mockUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSelectAll}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {selectedTasks.length === mockTasks.length
                    ? "Deselect All"
                    : "Select All"}
                </button>

                <button
                  onClick={handleBulkAssign}
                  disabled={selectedTasks.length === 0 || !selectedUser}
                  className="px-6 py-2 bg-custom-marian-blue text-white rounded-lg hover:bg-custom-marian-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Assign Tasks ({selectedTasks.length})
                </button>
              </div>
            </div>
          </div>

          {/* Task List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-custom-text-onyx">
                Available Tasks
              </h2>
              <p className="text-sm text-custom-text-gray-dark mt-1">
                Select tasks below to assign them to team members
              </p>
            </div>

            <div className="divide-y divide-gray-200">
              {mockTasks.map((task) => (
                <div
                  key={task.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={selectedTasks.includes(task.id)}
                      onChange={() => handleTaskSelection(task.id)}
                      className="h-4 w-4 text-custom-marian-blue focus:ring-custom-marian-blue border-gray-300 rounded"
                    />

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-custom-text-onyx">
                            {task.title}
                          </h3>
                          <p className="text-sm text-custom-text-gray-dark">
                            {task.location}
                          </p>
                        </div>

                        <div className="flex items-center gap-3">
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            {task.type}
                          </span>
                          <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                            {task.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Assignment History */}
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-custom-text-onyx">
                Recent Assignments
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {[
                  {
                    tasks: "3 Bridge Inspection Tasks",
                    assignee: "John Smith",
                    date: "July 15, 2025",
                    time: "10:30 AM",
                  },
                  {
                    tasks: "5 Road Maintenance Tasks",
                    assignee: "Sarah Johnson",
                    date: "July 14, 2025",
                    time: "2:15 PM",
                  },
                  {
                    tasks: "2 Conduit Inspection Tasks",
                    assignee: "Mike Davis",
                    date: "July 13, 2025",
                    time: "9:45 AM",
                  },
                  {
                    tasks: "4 Mixed Tasks",
                    assignee: "Lisa Wilson",
                    date: "July 12, 2025",
                    time: "11:20 AM",
                  },
                ].map((assignment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0"
                  >
                    <div>
                      <p className="text-sm font-medium text-custom-text-onyx">
                        {assignment.tasks}
                      </p>
                      <p className="text-sm text-custom-text-gray-dark">
                        Assigned to {assignment.assignee}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-custom-text-gray-dark">
                        {assignment.date}
                      </p>
                      <p className="text-xs text-custom-text-gray-dark">
                        {assignment.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
